import React, { FC, useMemo } from 'react';
import { usePostComposer } from 'app/modules/posts/modules/post/components/composer/PostComposerProvider';
import { useTranslation } from 'react-i18next';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import AccordionWidgetIcon from 'app/modules/posts/components/accordionWidget/AccordionWidgetIcon';
import IntercomUtils from 'app/utils/intercom';
import AccordionWidget from 'app/modules/posts/components/AccordionWidget';
import AccordionWidgetTitle from 'app/modules/posts/components/accordionWidget/AccordionWidgetTitle';
import TextEditor from 'app/modules/textEditor';
import { Chip } from '@kontentino/ui';
import { TextEditorFeature } from 'app/modules/textEditor/constants/textEditorFeature';
import { useQuery } from 'react-query';
import PageApi from 'api/page';
import { queryKey } from 'constants/queryKey';
import Skeleton from 'react-loading-skeleton';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { HyperText } from '@kontentino/kontentino-post-previews';

type Props = {
  accordionProps?: Partial<React.ComponentProps<typeof AccordionWidget>>;
};

const Notes: FC<Props> = ({ accordionProps }) => {
  const { t } = useTranslation();
  const { form, setForm } = usePostComposer();
  const { accountPlanType } = useSubscriptionInfo();
  const mentionableUsers = useQuery(queryKey.mentionableUsers, () =>
    PageApi.mentionableUsers(form.page.id),
  );

  return useMemo(
    () => (
      <AccordionWidget
        title={<AccordionWidgetTitle variant="notes" />}
        icon={<AccordionWidgetIcon variant="notes" />}
        titleSuffix={(isOpen) =>
          !isOpen &&
          form.note.length > 0 && (
            <Chip className="tw-bg-grayscale-10">
              <HyperText
                className="!tw-whitespace-nowrap !tw-text-black"
                options={{
                  ellipsis: true,
                  maxLength: 50,
                  mentionedUsers:
                    mentionableUsers.data?.map((user) => ({
                      id: user.id,
                      name: user.name,
                    })) || [],
                }}
              >
                {form.note}
              </HyperText>
            </Chip>
          )
        }
        data-name="posts-composer_notes"
        {...accordionProps}
      >
        <UserPermissionGate
          scopes={UserPermission.POST_EDIT_NOTE}
          deniedProps={{ disabled: true }}
        >
          <>
            {mentionableUsers.isLoading && <Skeleton height={300} />}
            {mentionableUsers.data !== undefined && (
              <TextEditor
                accountPlanType={accountPlanType}
                dataPrefix="post-form-notes"
                features={[TextEditorFeature.UserMention]}
                mentionableUsers={mentionableUsers.data}
                onFocus={() => {
                  IntercomUtils.track(
                    IntercomUtils.events.POST_NOTE_FIELD_FOCUSED,
                  );
                }}
                initialValue={form.note}
                onDebouncedChange={(note) => setForm({ note })}
                placeholder={t('writeYourNotes')}
                classNames={{
                  editor: 'tw-min-h-[100px]',
                }}
              />
            )}
          </>
        </UserPermissionGate>
      </AccordionWidget>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.note, mentionableUsers.data, accordionProps],
  );
};

export default Notes;
